import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AddressSelect } from "@/components/order/AddressSelect";
import { LmLogisticsInfo } from "@/components/order/LogisticsBudgetPanel";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { ApprovalNote, CancelPlanDialog, changeable, needsApproval } from "@/components/planning/PlanChange";

const today = () => new Date().toISOString().slice(0, 10);

// qty still free to plan per LM line: LM balance minus what other open plans still hold (excluding the plan being edited)
export const freeQty = (order, plans, skipId) => {
  const held = {};
  (plans || []).filter((p) => p.id !== skipId && !["closed", "cancelled"].includes(p.planning_status))
    .forEach((p) => p.lines.forEach((ln) => { held[ln.line_item_id] = (held[ln.line_item_id] || 0) + (ln.balance_qty || 0); }));
  return Object.fromEntries(order.line_items.map((ln) => [ln.id, Math.max(ln.qty - (ln.dispatched_qty || 0) - (held[ln.id] || 0), 0)]));
};

function PlanLines({ order, rows, setRows, free }) {
  const set = (id, k, v) => setRows({ ...rows, [id]: { ...rows[id], [k]: v } });
  return (
    <div className="overflow-x-auto rounded border border-slate-200">
      <table className="w-full text-sm">
        <thead><tr className="th text-left"><th className="px-2 py-2">#</th><th className="px-2 py-2">Item</th><th className="px-2 py-2 text-right">Ordered</th><th className="px-2 py-2 text-right" title="LM balance minus quantity already in other open plans">Balance (unplanned)</th><th className="px-2 py-2">Planned dispatch qty</th><th className="px-2 py-2">Remarks</th></tr></thead>
        <tbody>{order.line_items.map((ln) => {
          const r = rows[ln.id] || {};
          return (
            <tr key={ln.id} className="border-b border-slate-100" data-testid={`plan-line-${ln.line_no}`}>
              <td className="px-2 py-1 text-xs">{ln.line_no}</td>
              <td className="px-2 py-1 text-xs min-w-[180px]"><span className="font-mono">{ln.item_code}</span> {ln.description}</td>
              <td className="px-2 py-1 text-right font-mono text-xs">{ln.qty} {ln.uom}</td>
              <td className="px-2 py-1 text-right font-mono text-xs" data-testid={`plan-balance-${ln.line_no}`}>{free ? free[ln.id] ?? 0 : "…"}</td>
              <td className="px-2 py-1"><Input type="number" min="0" max={free ? Math.max(free[ln.id] ?? 0, 0) : undefined} className="w-20 h-8" value={r.planned_dispatch_qty ?? ""} onChange={(e) => set(ln.id, "planned_dispatch_qty", e.target.value)} data-testid={`plan-qty-${ln.line_no}`} /></td>
              <td className="px-2 py-1"><Input className="h-8 min-w-[120px]" value={r.remarks || ""} onChange={(e) => set(ln.id, "remarks", e.target.value)} data-testid={`plan-remarks-${ln.line_no}`} /></td>
            </tr>
          );
        })}</tbody>
      </table>
    </div>
  );
}

export function InvoicePicks({ orderId, value, onChange }) {
  const { data } = useFetch(`/invoices?order_id=${orderId}`);
  const rows = (data || []).filter((i) => i.status === "active");
  const flip = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  if (!rows.length) return <p className="text-xs text-slate-500" data-testid="plan-no-invoices">No invoices on this LM yet - add them in the LM's Invoices tab; the plan can be linked later.</p>;
  return (
    <div className="flex flex-wrap gap-2" data-testid="plan-invoice-picks">
      {rows.map((i) => (
        <label key={i.id} className={`flex items-center gap-2 rounded border px-2 py-1 text-xs cursor-pointer ${value.includes(i.id) ? "border-sky-500 bg-sky-50" : "border-slate-200"}`}>
          <input type="checkbox" checked={value.includes(i.id)} onChange={() => flip(i.id)} data-testid={`plan-invoice-${i.invoice_no}`} />
          <span className="font-mono">{i.invoice_no}</span><span className="text-slate-500">{fmtDate(i.invoice_date)}</span>
        </label>
      ))}
    </div>
  );
}

// new plan from the LM: mode "part" (empty qty) | "full" (unplanned balance prefilled); required-by locked to the LM date
export function PlanDispatchDialog({ order, plan, onClose, onSaved, mode, plans: given, allowCancel = true }) {
  const isNew = !plan;
  const { data: fetched } = useFetch(given ? null : `/planned-dispatches?lm_id=${order.id}`);
  const plans = given || fetched;
  const free = plans ? freeQty(order, plans, plan?.id) : null;
  const toQcOk = isNew || plan.planning_status === "draft";
  const lockDate = toQcOk;
  const [f, setF] = useState({ dispatch_address_id: "", expected_dispatch_date: today(), customer_required_by_date: order.expected_dispatch_date || "", invoice_ids: [], remarks: "" });
  const [rows, setRows] = useState(() => (isNew && mode === "full" && free ? Object.fromEntries(order.line_items.filter((ln) => free[ln.id] > 0).map((ln) => [ln.id, { planned_dispatch_qty: free[ln.id] }])) : {}));
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [cancel, setCancel] = useState(false);
  const reasonNeeded = plan && needsApproval(plan);
  useEffect(() => {
    if (!plan) return;
    setF({ dispatch_address_id: plan.dispatch_address_id, expected_dispatch_date: plan.expected_dispatch_date, customer_required_by_date: plan.planning_status === "draft" ? order.expected_dispatch_date || "" : plan.customer_required_by_date || "", invoice_ids: plan.invoice_source === "plan" ? (plan.invoices || []).map((i) => i.id) : [], remarks: plan.remarks || "" });
    setRows(Object.fromEntries(plan.lines.map((l) => [l.line_item_id, { planned_dispatch_qty: l.planned_dispatch_qty, remarks: l.remarks }])));
  }, [plan, order.expected_dispatch_date]);
  const sendToQc = async (p) => {
    try {
      await api.post("/qc-submissions", { lm_id: order.id, qc_scope_type: "PlannedDispatchSelection", planned_dispatch_ids: [p.id], remarks: f.remarks || "" });
      toast.success(`${p.planned_dispatch_number} planned and sent to QC`);
    } catch (e) {
      toast.error(`${p.planned_dispatch_number} planned, but not sent to QC: ${errMsg(e)} - submit it from Ready for QC`);
    }
  };
  const save = async (planning_status, toQc = false) => {
    const lines = Object.entries(rows).filter(([, r]) => Number(r.planned_dispatch_qty) > 0)
      .map(([id, r]) => ({ line_item_id: id, planned_dispatch_qty: Number(r.planned_dispatch_qty), remarks: r.remarks || "" }));
    setBusy(true);
    try {
      if (reasonNeeded && reason.trim().length < 3) { toast.error("Enter the reason for this change"); return; }
      const { customer_required_by_date, ...rest } = f;
      const body = isNew ? { ...rest, lm_id: order.id, planning_status, lines, source: "lm_plan" } : { ...f, lm_id: order.id, planning_status, lines, change_reason: reason };
      const r = plan ? await api.put(`/planned-dispatches/${plan.id}`, body) : await api.post("/planned-dispatches", body);
      if (toQc) await sendToQc(r.data);
      else toast.success(r.data.change?.approval_retriggered ? `${r.data.planned_dispatch_number} saved - sent for re-approval` : `${r.data.planned_dispatch_number} saved`);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="plan-dispatch-dialog">
        <DialogHeader><DialogTitle>{plan ? `${plan.planning_status === "draft" ? "Confirm draft" : "Edit"} ${plan.planned_dispatch_number}` : mode ? `${mode === "full" ? "Full" : "Part"} Dispatch` : "Plan Dispatch"} · {order.order_no}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Dispatch Plan - save it, or save as planned and send it to QC in one step. QC releases it, then logistics groups it into a movement. Invoices stay on the LM; tick the ones that travel with this batch.</p>
        <LmLogisticsInfo ctx={order} testid="plan-lm-context" />
        <div className="grid md:grid-cols-3 gap-3">
          <AddressSelect orderId={order.id} value={f.dispatch_address_id} onChange={(v) => setF({ ...f, dispatch_address_id: v })} testid="plan-address-select" />
          <div><Label>Expected dispatch date *</Label><Input type="date" value={f.expected_dispatch_date} onChange={(e) => setF({ ...f, expected_dispatch_date: e.target.value })} data-testid="plan-date-input" /></div>
          <div><Label>Customer required by</Label><Input type="date" value={f.customer_required_by_date} readOnly={lockDate} disabled={lockDate} onChange={(e) => setF({ ...f, customer_required_by_date: e.target.value })} data-testid="plan-required-by-input" />{lockDate && <p className="mt-1 text-[11px] text-slate-500" data-testid="plan-required-by-note">From the LM's expected dispatch date</p>}</div>
        </div>
        <div><Label>Invoice(s) for this batch</Label><InvoicePicks orderId={order.id} value={f.invoice_ids} onChange={(v) => setF({ ...f, invoice_ids: v })} /></div>
        <PlanLines order={order} rows={rows} setRows={setRows} free={free} />
        {isNew && mode === "full" && free && !order.line_items.some((ln) => free[ln.id] > 0) && <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="plan-nothing-free">Nothing is left to plan on this LM - every balance is already in an open plan.</p>}
        <div><Label>Dispatch planning remarks</Label><Textarea rows={2} value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} data-testid="plan-remarks-input" /></div>
        {plan && <ApprovalNote p={plan} action="edit" />}
        {plan && <div><Label>Reason for change {reasonNeeded && "*"}</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="plan-change-reason" /></div>}
        <DialogFooter className="gap-2">
          {allowCancel && plan && changeable(plan) && <Button variant="ghost" className="sm:mr-auto text-red-700" disabled={busy} onClick={() => setCancel(true)} data-testid="plan-dialog-cancel-plan-btn">Cancel this plan</Button>}
          <Button variant="outline" disabled={busy} onClick={() => save("draft")} data-testid="plan-save-draft-btn">Save draft</Button>
          <Button variant={toQcOk ? "outline" : "default"} disabled={busy} onClick={() => save("planned")} data-testid="plan-save-btn">Save as planned</Button>
          {toQcOk && <Button disabled={busy} onClick={() => save("planned", true)} data-testid="plan-save-send-qc-btn">Save as planned + send to QC</Button>}
        </DialogFooter>
        {cancel && <CancelPlanDialog p={plan} onClose={() => setCancel(false)} onDone={() => { setCancel(false); onSaved?.(); }} />}
      </DialogContent>
    </Dialog>
  );
}
